import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {produce} from 'immer'
import ErrorOutline from '@mui/icons-material/ErrorOutline'
import { ACCOUNTING_FILES_MAX_UPLOADS } from '../../../constants'
import { ISydDocument } from 'models/shared.models'
import { UploadedFile } from 'models/general'
import {
  AccountingForm,
  IAccountingFileStatus,
  IAccountingStepContinueButtonProps,
  TAccountingFileTypes,
} from 'models/accounting.models'
import { ACCOUNTING_PROCESS_STEPS } from 'lib/enums/accounting.enums'
import {
  accountingStepFiveFirstFileCategoryType,
  accountingStepFiveFourthFileCategoryType,
  accountingStepFiveSecondFileCategoryType,
  accountingStepFiveThirdFileCategoryType,
  accountingStepFourAccountingDocumentation,
  getFilesToValidate,
  getInvalidFiles,
  getTotalFilesPerType,
  getUploadedAndValidatedFiles,
  getUploadedFiles,
  getUploadingFile,
  getValidFiles,
} from 'lib/utils/Accounting/accounting.utils'
import { errorToastOptions } from 'lib/utils/toast.utils'
import useDialog from 'hooks/Shared/useDialog'
import { useAccountingContext } from 'containers/Accounting/AccountingContainer/AccountingContainer'
import { TModalIcon } from 'components/Shared/SydDialog/SydDialog'
import useAccountingStepFourValidationSteps from './useAccountingStepFourValidationSteps'

export interface IAccountingStepFourState {
  clientCommentsDialog: boolean
  fileStatuses: IAccountingFileStatus[]
  limitFileView: boolean
  validCommentsDialog: boolean
  validRenameDialog: boolean
  selectedFile?: ISydDocument
}

export interface IUseAccountingstepFour extends IAccountingStepFourState {
  continueButtonProps: IAccountingStepContinueButtonProps
  filesToValidate: ISydDocument[]
  totalFiles: ISydDocument[]
  uploadedAndValidatedFiles: ISydDocument[]
  uploadedFiles: ISydDocument[]
  uploadedPreviousStepsRequiredFiles: ISydDocument[]
  validFiles: ISydDocument[]
  fileUploadHandler: (
    totalUploadItems: ISydDocument[],
    additionalValidFiles?: ISydDocument[],
    additionalInvalidFiles?: ISydDocument[]
  ) => (files: UploadedFile[]) => void
  handleCommentsFormSubmit: (form: any) => Promise<void>
  handleRenameFormSubmit: (form: any) => Promise<void>
  handleSkipSubmit: () => void
  updateStepFourState: (
    update: (draft: IAccountingStepFourState) => void
  ) => void
  comment?: string
  uploadingFile?: UploadedFile
}

const useAccountingStepFour = (): IUseAccountingstepFour => {
  const {
    currentAccountingProcess,
    filesInProcess,
    filesToUpload,
    fileUploadHandlerFactory,
    handleFileRename,
    handleStepSubmit,
  } = useAccountingContext()

  const comment = currentAccountingProcess?.comments

  const dialog = useDialog()

  const totalFiles = useMemo(
    () =>
      getTotalFilesPerType(
        filesInProcess,
        accountingStepFourAccountingDocumentation
      ),
    [filesInProcess]
  )

  const [accountingStepFourState, setAccountingStepFourState] =
    useState<IAccountingStepFourState>({
      clientCommentsDialog: false,
      fileStatuses: totalFiles.map(f => ({
        id: f.idWsfile,
        status: f.statusText,
      })),
      limitFileView: true,
      validCommentsDialog: false,
      validRenameDialog: false,
    })

  const { fileStatuses, selectedFile } = accountingStepFourState

  const updateStepFourState = (
    update: (draft: IAccountingStepFourState) => void
  ) => {
    setAccountingStepFourState(currentState => produce(currentState, update))
  }

  const uploadingFile = getUploadingFile(
    filesToUpload,
    accountingStepFourAccountingDocumentation
  )

  const uploadedFiles = getUploadedFiles(
    filesInProcess,
    accountingStepFourAccountingDocumentation
  )

  const filesToValidate = getFilesToValidate(
    filesInProcess,
    accountingStepFourAccountingDocumentation
  )
  const validFiles = getValidFiles(totalFiles, fileStatuses)

  const invalidFiles = getInvalidFiles(totalFiles, fileStatuses)

  const uploadedAndValidatedFiles = getUploadedAndValidatedFiles(
    filesInProcess,
    accountingStepFourAccountingDocumentation
  )

  const uploadedPreviousStepsRequiredFiles = filesInProcess.filter(
    f =>
      ![
        accountingStepFourAccountingDocumentation?.type,
        accountingStepFiveFirstFileCategoryType?.type,
        accountingStepFiveSecondFileCategoryType?.type,
        accountingStepFiveThirdFileCategoryType?.type,
        accountingStepFiveFourthFileCategoryType?.type,
      ].includes(f?.description as TAccountingFileTypes)
  )

  const { continueButtonProps } = useAccountingStepFourValidationSteps({
    fileStatuses,
    filesToValidate,
    invalidFiles,
    totalFiles,
    uploadedAndValidatedFiles,
    uploadedFiles,
    validFiles,
  })

  const fileUploadHandler = fileUploadHandlerFactory(
    ACCOUNTING_PROCESS_STEPS.SUBIDA_DOCUMENTOS_MULTIPLES,
    ACCOUNTING_FILES_MAX_UPLOADS
  )

  const handleCommentsFormSubmit = async (form: any) => {
    handleStepSubmit(
      ACCOUNTING_PROCESS_STEPS.SUBIDA_DOCUMENTOS_MULTIPLES,
      form.comment,
      [],
      invalidFiles
    )
    updateStepFourState(draft => {
      draft.clientCommentsDialog = false
    })
  }

  const handleRenameFormSubmit = async (form: AccountingForm) => {
    if (filesInProcess.some(f => f.name === form.name)) {
      toast.error(
        'No puedes subir más de un archivo con el mismo nombre durante el proceso',
        errorToastOptions
      )
      return
    }

    selectedFile && handleFileRename(form.name, selectedFile)
    updateStepFourState(draft => {
      draft.selectedFile = undefined
    })
  }

  const handleSkipSubmit = () => {
    dialog.danger({
      title: '¿Omitir la Documentación Contable?',
      message: 'Podrás subirlos en el apartado correspondiente',
      icon: ErrorOutline as TModalIcon,
      primaryButtonText: 'Omitir Documentación Contable',
      secondaryButtonText: 'Regresar',
      onPrimaryClick: () =>
        handleStepSubmit(ACCOUNTING_PROCESS_STEPS.SUBIDA_DOCUMENTOS_MULTIPLES),
    })
  }

  useEffect(() => {
    updateStepFourState(draft => {
      draft.fileStatuses = totalFiles.map(f => ({
        id: f.idWsfile,
        status: f.statusText,
      }))
    })
  }, [totalFiles, filesInProcess])

  return {
    ...accountingStepFourState,
    comment,
    continueButtonProps,
    filesToValidate,
    totalFiles,
    uploadedAndValidatedFiles,
    uploadedFiles,
    uploadingFile,
    uploadedPreviousStepsRequiredFiles,
    validFiles,
    fileUploadHandler,
    handleCommentsFormSubmit,
    handleRenameFormSubmit,
    handleSkipSubmit,
    updateStepFourState,
  }
}

export default useAccountingStepFour
